import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import RouteIcon from "@mui/icons-material/Route";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TradeBlotterTable from "./TradeBlotterTable";
import NewOrderDialog from "./NewOrderDialog";
import BulkEdit from "./Popup/BulkEdit";
import BulkDelete from "./Popup/BulkDelete";
import DeletePopUp from "./Popup/DeletePopUp";
import EditTradeDialog from "./Popup/EditTradeDialog";
import ManageFillsDialog from "./Popup/ManageFillsDialog";
import SummaryDialog from "./SummaryDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  hashAction,
  hashPosition,
  findStrategy,
  findFund,
  findCust,
  findBrokerAbb,
} from "../../component/Methods";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { toast } from "react-toastify";
import { useTradeBlotterState } from "../../hooks/useOptimizedState";
import { iteratee } from "lodash";
import { getPayloadSize, payloadQualifiesForS3Upload } from "../../component/Methods/checks";
import { uploadToS3 } from "../../utils/s3Upload";
// Loading actions are not available in todoActions - using local state instead

const TradeBlotter = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [selectedTicker, setSelectedTicker] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [newOrderDialogOpen, setNewOrderDialogOpen] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkArchive, setIsBulkArchive] = useState(false);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTradeForEdit, setSelectedTradeForEdit] = useState(null);
  const [manageFillsDialogOpen, setManageFillsDialogOpen] = useState(false);
  const [selectedTradeForFills, setSelectedTradeForFills] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);

  // Redux state - Using new slice system
  const { trades, deletes, brokers, strategies, funds, custodians, routes } = useTradeBlotterState();

  const { userInfo, apiInfo } = useSelector((state) => state?.users);

  // View state - Non-Deleted vs Deleted trades
  const [viewType, setViewType] = useState("non-deleted"); // "non-deleted" or "deleted"

  // Ref to access DataGrid Pro methods
  const dataGridRef = useRef(null);

  // State to track DataGrid Pro's internal state for visual feedback
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const handleEditTrade = useCallback((tradeData) => {
    setSelectedTradeForEdit(tradeData);
    setEditDialogOpen(true);
  }, []);

  const handleManageFills = useCallback((tradeData) => {
    setSelectedTradeForFills(tradeData);
    setManageFillsDialogOpen(true);
  }, []);

  const handleSubmitFills = useCallback(async (fillData) => {
    try {
      // Create the websocket message
      const message = {
        action: "modify_trade_fills",
        data: [fillData],
      };

      // Show loader and add to active requests
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Updating fills..."));
      dispatch(addActiveRequests("modify_trade_fills"));

      sendWebSocketMessage(JSON.stringify(message));

      return Promise.resolve();
    } catch (error) {
      console.error("Error submitting fills:", error);
      return Promise.reject(error);
    }
  }, []);

  // Function to transform Redux trade objects to table format
  const transformTradeData = (tradeObj) => {
    if (!tradeObj) {
      return null;
    }

    // Get the last order (most recent)
    const lastOrder =
      tradeObj.orders && tradeObj.orders.length > 0
        ? tradeObj.orders[tradeObj.orders.length - 1]
        : null;

    if (!lastOrder) {
      return null;
    }

    // Get the last manual or fill data
    const lastManual =
      tradeObj.manuals && tradeObj.manuals.length > 0
        ? tradeObj.manuals[tradeObj.manuals.length - 1]
        : null;

    const hasFill = tradeObj.fill && Object.keys(tradeObj.fill).length > 0;
    const hasManual = tradeObj.manuals && tradeObj.manuals.length > 0;

    // Get distribution data
    const distributionKeys = Object.keys(lastOrder.distribution || {});
    let fundName = "N/A";
    let strategyName = "N/A";
    let custodianName = "N/A";
    let totalOrderedShares = 0;

    // Determine trade type based on distribution count
    const distributionType = distributionKeys.length > 1 ? "P" : "S"; // P = Parent Trade, S = Single Trade

    if (distributionKeys.length > 0) {
      const firstKey = distributionKeys[0];
      const shareCount = lastOrder.distribution[firstKey];
      //loop through the distribution keys and add the share count
      for (const key of distributionKeys) {
        totalOrderedShares += lastOrder.distribution[key];
      }

      // Parse distribution key: "gs-wsf#927a4bd37ff00177aac2b4f92d9076f7#0#dc3abdd9a59aa6cbdcd7f5c43dade391"
      const parts = firstKey.split("#");
      if (parts.length >= 3) {
        const account = parts[0]; // "gs-wsf"
        const fundHash = parts[1]; // "927a4bd37ff00177aac2b4f92d9076f7"
        const entityType = parts[2]; // "0" (fund) or "1" (SMA)
        const strategyHash = parts[3]; // "dc3abdd9a59aa6cbdcd7f5c43dade391"

        // Find fund name
        fundName = findFund(fundHash, funds) || "N/A";

        // Find strategy name
        if (entityType === "1") {
          strategyName = "CPFactorBW";
        } else {
          strategyName = findStrategy(strategyHash, strategies) || "N/A";
        }

        // Find custodian name by account
        // First find the fund to get its accounts
        const selectedFund = funds.find((fund) => fund.fundId === fundHash);
        if (
          selectedFund &&
          selectedFund.accounts &&
          selectedFund.accounts[account]
        ) {
          // Get the custodian ID from the fund's account
          const custodianId = selectedFund.accounts[account].custodian;
          // Use the custodian ID to find the custodian name
          custodianName = findCust(custodianId, custodians) || "N/A";
        } else {
          custodianName = "N/A";
        }
      }
    }

    // Determine broker abbreviation for filled percentage logic
    const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
    const isBrokerCross = brokerAbb === "cros";

    // Determine trade state and filled data
    let state = "Working";
    let filledPercent = 0;
    let pricePerShare = 0;
    let filledShares = 0;
    let quantity = totalOrderedShares; // Always keep as total ordered shares

    if (hasManual) {

      // Manual fill
      pricePerShare = lastManual.sharePrice || 0;
      filledShares = lastManual.shareCount || 0;

      if (totalOrderedShares === filledShares) {
        state = "Ready";
      }

      // For broker crosses, always show as 100% filled and ready
      if (isBrokerCross) {
        filledPercent = 100;
        state = "Ready";
      } else {
        // Calculate actual percentage: filled shares / total ordered shares
        filledPercent = totalOrderedShares > 0
          ? Math.round((filledShares / totalOrderedShares) * 100)
          : 0;
      }
    } else if (hasFill) {
      //state shound be ready only if all the shares are filled
      // Auto fill
      pricePerShare = tradeObj.fill.sharePrice || 0;
      filledShares = tradeObj.fill.shareCount || 0;

      if (totalOrderedShares === filledShares) {
        state = "Ready";
      }

      // For broker crosses, always show as 100% filled and ready
      if (isBrokerCross) {
        filledPercent = 100;
        state = "Ready";
      } else {
        // Calculate actual percentage: filled shares / total ordered shares
        filledPercent = totalOrderedShares > 0
          ? Math.round((filledShares / totalOrderedShares) * 100)
          : 0;
      }
    } else {
      // Working order
      state = "Working";
      // For broker crosses, always show as 100% filled and ready
      if (isBrokerCross) {
        filledPercent = 100;
        state = "Ready";
      } else {
        filledPercent = 0;
      }
      pricePerShare = 0;
      filledShares = 0;
    }

    // Determine side from action
    const action = hashAction(lastOrder.action);
    const position = hashPosition(lastOrder.position);

    // Create proper trade type display
    let tradeType = "";
    let isOpeningPosition = false;

    if (action === "Buy" && position === "Long") {
      tradeType = "BUY LONG";
      isOpeningPosition = true;
    } else if (action === "Sell" && position === "Short") {
      tradeType = "SELL SHORT";
      isOpeningPosition = true;
    } else if (action === "Buy" && position === "Short") {
      tradeType = "COVER SHORT";
      isOpeningPosition = false;
    } else if (action === "Sell" && position === "Long") {
      tradeType = "SELL LONG";
      isOpeningPosition = false;
    } else {
      tradeType = `${action} ${position}`;
      isOpeningPosition = action === "Buy";
    }

    const side = tradeType; // Use tradeType instead of simple buy/sell

    // Determine order type
    const orderType = lastOrder.type === 1 ? "Market" : "Limit";
    const routing = lastOrder.routing;

    // Determine if ticker is routable based on investment type
    // Investment types 8 (MoneyMarket), 9 (MutualFund), 11 (Partnership) are not routable
    const tickerInvestment = lastOrder.ticker?.investment;
    const routable = (tickerInvestment === 8 || tickerInvestment === 9 || tickerInvestment === 11) ? "No" : "Yes";

    // Get commission per share
    const commissionPerShare = tradeObj.commission?.commission || 0;

    // Format commission to 4 decimal places for better visibility
    const formattedCommission = Number(commissionPerShare).toFixed(4);

    // Format timestamp from lastOrder.date
    const formatTimestamp = (date) => {
      if (!date) return "00:00:00";
      const dateObj = new Date(date);
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      const seconds = String(dateObj.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    };

    const transformedTrade = {
      id: tradeObj.tradeId,
      serialNumber: 1, // This will be set by the table
      timestamp: formatTimestamp(lastOrder?.date),
      ticker: lastOrder.ticker?.symbol?.toUpperCase() || "N/A",
      fund: fundName,
      strategy: strategyName,
      state: state,
      side: side,
      routing: routing,
      routable: routable,
      investment: tickerInvestment,
      isOpeningPosition: isOpeningPosition, // Add this flag for color coding
      quantity: totalOrderedShares, // Always show total ordered shares (1400)
      filledShares: filledShares, // Show actual filled shares (100)
      orderType: orderType,
      pricePerShare: pricePerShare,
      commPerShare: formattedCommission,
      custodian: custodianName,
      broker: (() => {
        // Try to find broker by ID first
        let brokerName = findBrokerAbb(lastOrder.broker, brokers);
        if (brokerName) return brokerName;

        // If still not found, return the original value or "N/A"
        return "N/A";
      })(),
      filledPercent: filledPercent,
      expandable: true,
      // Additional fields for reference
      tradeId: tradeObj.tradeId,
      orderDate: lastOrder.date,
      action: action,
      position: position,
      totalOrderedShares: totalOrderedShares,
      // Add process information to determine if edit should be disabled
      hasProcess: !!lastOrder.process,
      processId: lastOrder.process || null,
      // Add user information for websocket requests
      user: lastOrder.user || "asfand@plutus21.com",
      // Add distribution type (Single Trade vs Parent Trade)
      distributionType: distributionType,
      // Add order date for daily counting
      orderDate: lastOrder.date,
      // Add child trades information for Parent Trades
      childTrades: (() => {
        if (distributionType === "P") {
          return Object.entries(lastOrder.distribution || {})
            .map(([distributionKey, shareCount]) => {
              // Parse distribution key: "gs-wsf#927a4bd37ff00177aac2b4f92d9076f7#0#dc3abdd9a59aa6cbdcd7f5c43dade391"
              const parts = distributionKey.split("#");
              if (parts.length >= 4) {
                const account = parts[0]; // "gs-wsf"
                const fundHash = parts[1]; // "927a4bd37ff00177aac2b4f92d9076f7"
                const entityType = parts[2]; // "0" (fund) or "1" (SMA)
                const strategyHash = parts[3]; // "dc3abdd9a59aa6cbdcd7f5c43dade391"

                // Find fund name
                const childFundName = findFund(fundHash, funds) || "N/A";

                // Find strategy name
                const childStrategyName = entityType === "1" 
                  ? "CPFactorBW"
                  : (findStrategy(strategyHash, strategies) || "N/A");

                // Find custodian name by account
                let childCustodianName = "N/A";
                const selectedFund = funds.find(
                  (fund) => fund.fundId === fundHash
                );
                if (
                  selectedFund &&
                  selectedFund.accounts &&
                  selectedFund.accounts[account]
                ) {
                  const custodianId = selectedFund.accounts[account].custodian;
                  childCustodianName =
                    findCust(custodianId, custodians) || "N/A";
                }

                return {
                  account,
                  fund: childFundName,
                  strategy: childStrategyName,
                  custodian: childCustodianName,
                  sharesOrdered: shareCount,
                  sharesFilled: 0, // This would need to be calculated from fills/manuals
                  filledPercent: 0, // This would need to be calculated
                  distributionKey,
                };
              }
              return null;
            })
            .filter(Boolean);
        }
        return [];
      })(),
      // Add fields needed for EditTradeDialog
      fundAbbreviation: (() => {
        // Find fund abbreviation from fund name
        const foundFund = funds.find((f) => f.name === fundName);
        return foundFund?.abbreviation || "";
      })(),
      brokerAbbreviation: (() => {
        // Get broker abbreviation directly from the broker ID
        let brokerName = findBrokerAbb(lastOrder.broker, brokers);
        return brokerName || "";
      })(),
      account: (() => {
        // Extract account from distribution key
        if (distributionKeys.length > 0) {
          const firstKey = distributionKeys[0];
          const parts = firstKey.split("#");
          return parts[0] || ""; // "gs-wsf"
        }
        return "";
      })(),
      // Add edit handler
      onEditClick: handleEditTrade,
      // Add manage fills handler
      onManageFillsClick: handleManageFills,
    };

    return transformedTrade;
  };

  // Transform Redux trades to table format
  const transformedData = useMemo(() => {
    // Choose data source based on view type
    const dataSource = viewType === "deleted" ? deletes : trades;

    if (!dataSource || dataSource.length === 0) {
      return [];
    }

    // Group trades by date for daily counting
    const tradesByDate = {};

    dataSource.forEach((tradeObj) => {
      const transformedTrade = transformTradeData(tradeObj);
      if (transformedTrade && transformedTrade.orderDate) {
        // Convert timestamp to date string (YYYY-MM-DD format)
        const tradeDate = new Date(transformedTrade.orderDate)
          .toISOString()
          .split("T")[0];

        if (!tradesByDate[tradeDate]) {
          tradesByDate[tradeDate] = [];
        }
        tradesByDate[tradeDate].push(transformedTrade);
      }
    });

    // Assign daily serial numbers and flatten the array
    const transformed = Object.entries(tradesByDate).flatMap(
      ([date, dateTrades]) => {
        return dateTrades.map((trade, index) => ({
          ...trade,
          serialNumber: index + 1, // Daily counter starting from 1
        }));
      }
    );

    return transformed;
  }, [
    trades,
    deletes,
    viewType,
    brokers,
    strategies,
    funds,
    custodians,
    handleEditTrade,
    handleManageFills,
  ]);

  // Get unique values for dropdowns
  const uniqueFunds = useMemo(() => {
    const fundSet = new Set();
    transformedData.forEach((trade) => {
      if (trade.fund !== "N/A") {
        fundSet.add(trade.fund);
      }
    });
    return Array.from(fundSet);
  }, [transformedData]);

  const uniqueStrategies = useMemo(() => {
    const strategySet = new Set();
    transformedData.forEach((trade) => {
      if (trade.strategy !== "N/A") {
        strategySet.add(trade.strategy);
      }
    });
    return Array.from(strategySet);
  }, [transformedData]);

  const uniqueTickers = useMemo(() => {
    // First filter by selected date to only show tickers for that date
    let filteredByDate = transformedData;
    if (selectedDate) {
      const selectedDateStart = new Date(selectedDate);
      selectedDateStart.setHours(0, 0, 0, 0);

      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);

      filteredByDate = transformedData.filter((item) => {
        if (!item.orderDate) {
          return false;
        }
        const orderDate = new Date(item.orderDate);
        return orderDate >= selectedDateStart && orderDate <= selectedDateEnd;
      });
    }

    // Also consider other filters except ticker itself
    let filteredData = filteredByDate;
    if (searchTerm) {
      filteredData = filteredData.filter(
        (item) =>
          item.ticker.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.fund.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.strategy.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.side.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedEntity) {
      filteredData = filteredData.filter((item) => item.fund === selectedEntity);
    }

    if (selectedStrategy) {
      filteredData = filteredData.filter((item) => item.strategy === selectedStrategy);
    }

    const tickerSet = new Set();
    filteredData.forEach((trade) => {
      if (trade.ticker !== "N/A") {
        tickerSet.add(trade.ticker);
      }
    });

    // Custom sort: letters first, then numbers
    return Array.from(tickerSet).sort((a, b) => {
      const aIsNumber = /^\d/.test(a);
      const bIsNumber = /^\d/.test(b);

      if (aIsNumber && !bIsNumber) return 1; // a is number, b is letter -> a comes after b
      if (!aIsNumber && bIsNumber) return -1; // a is letter, b is number -> a comes before b
      return a.localeCompare(b); // both same type, sort alphabetically/numerically
    });
  }, [transformedData, selectedDate, searchTerm, selectedEntity, selectedStrategy]);

  const filteredData = useMemo(() => {
    let data = transformedData;

    // Date filtering - show trades for the selected date
    if (selectedDate) {
      // Memoize date range calculations
      const selectedDateStart = new Date(selectedDate);
      selectedDateStart.setHours(0, 0, 0, 0);

      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);

      data = data.filter((item) => {
        if (!item.orderDate) {
          return false;
        }

        const orderDate = new Date(item.orderDate);
        return orderDate >= selectedDateStart && orderDate <= selectedDateEnd;
      });
    }

    if (searchTerm) {
      data = data.filter(
        (item) =>
          item.ticker.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.fund.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.strategy.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.side.toLowerCase().includes(searchTerm.toLowerCase()) // Include trade type in search
      );
    }

    if (selectedEntity) {
      data = data.filter((item) => item.fund === selectedEntity);
    }

    if (selectedStrategy) {
      data = data.filter((item) => item.strategy === selectedStrategy);
    }

    if (selectedTicker) {
      data = data.filter((item) => item.ticker === selectedTicker);
    }

    return data;
  }, [
    transformedData,
    searchTerm,
    selectedEntity,
    selectedStrategy,
    selectedTicker,
    selectedDate,
  ]);

  // Memoize formatted date strings to avoid recalculating on every render
  const formattedSelectedDate = useMemo(() => {
    return selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [selectedDate]);

  const shortFormattedDate = useMemo(() => {
    return selectedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate]);

  // Memoize date input value to avoid recalculating on every render
  const dateInputValue = useMemo(() => {
    return selectedDate.toISOString().split("T")[0];
  }, [selectedDate]);

  const handleNewOrder = () => {
    setNewOrderDialogOpen(true);
  };

  const handleNewOrderSubmit = (orderData) => {
    // TODO: Implement order creation logic
    // This will be integrated with the backend later
  };


  const handleUpdateSelected = () => {
    setBulkEditOpen(true);
  };

  const handleDeleteSelected = () => {
    setIsBulkArchive(false); // Ensure delete mode
    setBulkDeleteOpen(true);
  };

  const handleArchiveSelected = () => {

    const anyUnfilled = selectedTrades.some(trade => trade.filledPercent < 100);
    if (anyUnfilled) {
      toast.error('Cannot archive unfilled trades.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else {
      setIsBulkArchive(true); // Set archive mode
      setBulkDeleteOpen(true);
    }
  };

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
    // Clear selection after bulk edit completes
    if (dataGridRef.current) {
      dataGridRef.current.clearSelection();
    }
  };

  const handleBulkDeleteClose = () => {
    setBulkDeleteOpen(false);
    setIsBulkArchive(false);
    // Clear selection after bulk delete/archive completes
    if (dataGridRef.current) {
      dataGridRef.current.clearSelection();
    }
  };

  const handleRouteSelect = () => {
    //setDeletePopUpOpen(true);
  };

  const handleTradeSelectionChange = (newSelection) => {
    // newSelection contains the row IDs from DataGrid
    // Find the corresponding trade objects from filteredData
    const selectedTradeObjects = newSelection
      .map((rowId) => filteredData.find((trade) => trade.id === rowId))
      .filter(Boolean);

    // Find the original Redux trade objects that have the orders property
    const originalTradeObjects = selectedTradeObjects
      .map((transformedTrade) => {
        const originalTrade = trades.find(
          (trade) => trade.tradeId === transformedTrade.tradeId
        );

        return {
          tradeObject: originalTrade, // The original Redux trade object with orders
          tradeId: transformedTrade.tradeId, // The actual trade ID
          filledPercent: transformedTrade.filledPercent, // Include filledPercent
        };
      })
      .filter((item) => item.tradeObject); // Only include items that have the original trade object

    setSelectedTrades(originalTradeObjects);
  };



  const updateCommissionForEligibleTrades = async () => {

    if (!Array.isArray(trades) || trades.length === 0) return;
    const eligibleData = trades.filter((item) => {
      // Remove cros or empty broker IDs
      const lastOrder =
        item.orders && item.orders.length > 0
          ? item.orders[item.orders.length - 1]
          : null;

      if (!lastOrder) {
        return false;
      }

      let brokerAbb = findBrokerAbb(lastOrder.broker, brokers);

      if (brokerAbb == null || brokerAbb === "cros") return false;


      const lastOrderDate = new Date(lastOrder.date);
      if (lastOrderDate.toDateString() !== selectedDate.toDateString()) return false;

      // Take all other trades
      return true;
    });



    if (eligibleData.length === 0) {
      toast.error(
        "No eligible trades found with Cantor or Goldman Sachs brokers"
      );
      return;
    }
    const commissionPayload = eligibleData.map((trade) => {
      const lastOrder =
        trade.orders && trade.orders.length > 0
          ? trade.orders[trade.orders.length - 1]
          : null;


      const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
      const brokerLower = brokerAbb.toLowerCase();

      let commission = 0;
      if (brokerLower === "gspt") {
        commission = 0.01;
      } else if (brokerLower == "cfr") {
        commission = 0.0065;
      }

      return {
        trade: trade.tradeId,
        commission,
      };
    });


    const webSocketPayload = {
      action: "modify_trade_commissions",
      data: commissionPayload,
    };

    if (payloadQualifiesForS3Upload(getPayloadSize(webSocketPayload))) {
      try {
        const s3Result = await uploadToS3(
          webSocketPayload,
          apiInfo,
          userInfo,
          "modify_trade_commissions"
        );

        if (s3Result.success) {
          const s3ReferencePayload = {
            action: "modify_trade_commissions",
            file: s3Result.fileName,
          };

          sendWebSocketMessage(JSON.stringify(s3ReferencePayload));


        } else {
          toast.error(
            "Failed to upload commission data to S3. Please try again later."
          );
          return;
        }
      } catch (error) {
        toast.error(
          "Failed to upload commission data to S3. Please try again later."
        );
        return;
      }
    } else {
      const payloadString = JSON.stringify(webSocketPayload);
      sendWebSocketMessage(payloadString);
    }

    dispatch(addActiveRequests("modify_trade_commissions"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Auto Filling Broker Commissions"));
  };


  return (
    <>
      <Helmet>
        <title>
          {viewType === "deleted" ? "Deleted Trades" : "Trade Blotter"} |
          Vantage Alpha
        </title>
      </Helmet>

      <Box sx={{ width: "100%", overflow: "hidden" }}>
        {/* Main Content */}
        <Box
          sx={{
            width: "100%",
            height: "calc(100vh + 150px)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {/* Search and Controls Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {/* Top Row - Search and Main Controls */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* Left Side - Search */}
              <TextField
                size="small"
                placeholder="Search tickers, funds, or strategies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.disabled }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 400,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.paper,
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />

              {/* Right Side - Status and Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* View Type Indicator */}
                <Chip
                  label={`${viewType === "deleted" ? "Deleted" : "Active"
                    } Trades (${filteredData.length})`}
                  color={viewType === "deleted" ? "error" : "success"}
                  size="small"
                  variant="outlined"
                />

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    Trade View
                  </InputLabel>
                  <Select
                    value={viewType}
                    onChange={(e) => setViewType(e.target.value)}
                    label="Trade View"
                    sx={{
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                    }}
                  >
                    <MenuItem value="non-deleted">Active Trades</MenuItem>
                    <MenuItem value="deleted">Deleted Trades</MenuItem>
                  </Select>
                </FormControl>

                {/* Help text */}
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                    maxWidth: 200,
                    lineHeight: 1.2,
                  }}
                >
                  Switch between active and deleted trades for the selected date
                </Typography>
              </Box>
            </Box>

            {/* Bottom Row - Filters and Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left Side - Filters */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    FUNDS
                  </InputLabel>
                  <Select
                    value={selectedEntity}
                    onChange={(e) => setSelectedEntity(e.target.value)}
                    label="FUNDS"
                    sx={{
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                    }}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {uniqueFunds.map((fund) => (
                      <MenuItem key={fund} value={fund}>
                        {fund}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    STRATEGIES
                  </InputLabel>
                  <Select
                    value={selectedStrategy}
                    onChange={(e) => setSelectedStrategy(e.target.value)}
                    label="STRATEGIES"
                    sx={{
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                    }}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {uniqueStrategies.map((strategy) => (
                      <MenuItem key={strategy} value={strategy}>
                        {strategy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    TICKERS
                  </InputLabel>
                  <Select
                    value={selectedTicker}
                    onChange={(e) => setSelectedTicker(e.target.value)}
                    label="TICKERS"
                    sx={{
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                    }}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {uniqueTickers.map((ticker) => (
                      <MenuItem key={ticker} value={ticker}>
                        {ticker}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ minWidth: 100 }}>
                  <TextField
                    type="date"
                    label="DATE"
                    size="small"
                    fullWidth
                    value={dateInputValue}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                    sx={{
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                    }}
                  />
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedDate(new Date())}
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    px: 2,
                  }}
                >
                  Today
                </Button>

                {/* Filter Icon - Triggers DataGrid's built-in filter panel */}
                <Tooltip title="Advanced Filters">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (dataGridRef.current) {
                        // Toggle the filter panel
                        dataGridRef.current.toggleFilterPanel();
                      }
                    }}
                    sx={{
                      borderRadius: 1,
                      color: theme.palette.text.primary,
                      position: "relative",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <FilterListIcon />
                    {/* Filter indicator badge */}
                    {hasActiveFilters && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          backgroundColor: theme.palette.error.main,
                          color: "white",
                          borderRadius: "50%",
                          width: 18,
                          height: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        !
                      </Box>
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Right Side - Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Action Buttons - Always visible but only enabled when trades are selected */}

                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => setSummaryDialogOpen(true)}
                  disabled={filteredData.length === 0}
                  sx={{
                    borderRadius: "12px !important",
                    textTransform: "none !important",
                    fontWeight: "600 !important",
                    px: "16px !important",
                    borderColor: "#009688 !important",
                    color: "#009688 !important",
                    background: "transparent !important",
                    backgroundImage: "none !important",
                    "&:hover": {
                      borderColor: "#009688 !important",
                      background: `linear-gradient(135deg, #4db6ac 0%, #009688 100%) !important`,
                      backgroundImage: "none !important",
                      color: "#ffffff !important",
                    },
                    "&:disabled": {
                      borderColor: `${theme.palette.action.disabled} !important`,
                      color: `${theme.palette.action.disabled} !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                    "&.MuiButton-outlined": {
                      borderColor: "#009688 !important",
                      color: "#009688 !important",
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                  }}
                >
                  View Summary
                </Button>

                {/* I want this button to be of some other color */}
                <Button
                  variant="outlined"
                  // suggest some other icon instead of edit icon
                  startIcon={<AutoFixHighIcon />}
                  onClick={updateCommissionForEligibleTrades}
                  sx={{
                    borderRadius: "12px !important",
                    textTransform: "none !important",
                    fontWeight: "600 !important",
                    px: "16px !important",
                    borderColor: "#ff9800 !important",
                    color: "#ff9800 !important",
                    background: "transparent !important",
                    backgroundImage: "none !important",
                    "&:hover": {
                      borderColor: "#ff9800 !important",
                      background: `linear-gradient(135deg, #ffb74d 0%, #ff9800 100%) !important`,
                      backgroundImage: "none !important",
                      color: "#ffffff !important",
                    },
                    "&:disabled": {
                      borderColor: `${theme.palette.action.disabled} !important`,
                      color: `${theme.palette.action.disabled} !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                      "&.MuiButton-outlined": {
                        borderColor: "#ff9800 !important",
                        color: "#ff9800 !important",
                        background: "transparent !important",
                        backgroundImage: "none !important",
                      },
                  }}
                >
                  Auto Fill Commissions
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleUpdateSelected}
                  disabled={selectedTrades.length === 0}
                  sx={{
                    borderRadius: "12px !important",
                    textTransform: "none !important",
                    fontWeight: "600 !important",
                    px: "16px !important",
                    borderColor: `#d8e10d !important`,
                    color: `#d8e10d !important`,
                    background: "transparent !important",
                    backgroundImage: "none !important",
                    "&:hover": {
                      borderColor: "#00ff88 !important",
                      background: `linear-gradient(135deg, #00ff88 0%, #00cc6a 100%) !important`,
                      backgroundImage: "none !important",
                      color: "#000000 !important",
                    },
                    
                    "&.MuiButton-outlined": {
                      borderColor: `#d8e10d !important`,
                      color: `#d8e10d !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                    "&:disabled": {
                      borderColor: `${theme.palette.action.disabled} !important`,
                      color: `${theme.palette.action.disabled} !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                  }}
                >
                  Update Selected
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  disabled={selectedTrades.length === 0}
                  sx={{
                    borderRadius: "12px !important",
                    textTransform: "none !important",
                    fontWeight: "600 !important",
                    px: "16px !important",
                    borderColor: `${theme.palette.action.disabled} !important`,
                    color: `${theme.palette.action.disabled} !important`,
                    background: "transparent !important",
                    backgroundImage: "none !important",
                    "&:hover": {
                      borderColor: "#d32f2f !important",
                      background: `linear-gradient(135deg, #ef5350 0%, #d32f2f 100%) !important`,
                      backgroundImage: "none !important",
                      color: "#ffffff !important",
                    },
                    "&.MuiButton-outlined": {
                      borderColor: "#d32f2f !important",
                      color: "#d32f2f !important",
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                    "&:disabled": {
                      borderColor: `${theme.palette.action.disabled} !important`,
                      color: `${theme.palette.action.disabled} !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                  }}
                >
                  Delete Selected
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ArchiveIcon />}
                  onClick={handleArchiveSelected}
                  disabled={selectedTrades.length === 0}
                  sx={{
                    borderRadius: "12px !important",
                    textTransform: "none !important",
                    fontWeight: "600 !important",
                    px: "16px !important",
                    borderColor: `${theme.palette.action.disabled} !important`,
                    color: `${theme.palette.action.disabled} !important`,
                    background: "transparent !important",
                    backgroundImage: "none !important",
                    "&:hover": {
                      borderColor: "#1976d2 !important",
                      background: `linear-gradient(135deg, #42a5f5 0%, #1976d2 100%) !important`,
                      backgroundImage: "none !important",
                      color: "#ffffff !important",
                    },
                    "&.MuiButton-outlined": {
                      borderColor: "#1976d2 !important",
                      color: "#1976d2 !important",
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                    "&:disabled": {
                      borderColor: `${theme.palette.action.disabled} !important`,
                      color: `${theme.palette.action.disabled} !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },

                  }}
                >
                  Archive Selected
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<RouteIcon />}
                  onClick={handleRouteSelect}
                  disabled={selectedTrades.length === 0}
                  sx={{
                    borderRadius: "12px !important",
                    textTransform: "none !important",
                    fontWeight: "600 !important",
                    px: "16px !important",
                    borderColor: "#4caf50 !important",
                    color: "#4caf50 !important",
                    background: "transparent !important",
                    backgroundImage: "none !important",
                    "&:hover": {
                      borderColor: "#4caf50 !important",
                      background: `linear-gradient(135deg, #81c784 0%, #4caf50 100%) !important`,
                      backgroundImage: "none !important",
                      color: "#ffffff !important",
                    },
                    "&.MuiButton-outlined": {
                      borderColor: "#4caf50 !important",
                      color: "#4caf50 !important",
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                    "&:disabled": {
                      borderColor: `${theme.palette.action.disabled} !important`,
                      color: `${theme.palette.action.disabled} !important`,
                      background: "transparent !important",
                      backgroundImage: "none !important",
                    },
                  }}
                >
                  Route Selected
                </Button>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewOrder}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    backgroundColor: theme.palette.primary.main,
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  New order
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {/* Date Summary */}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Showing {viewType === "deleted" ? "deleted" : "active"} trades
                for: <strong>{formattedSelectedDate}</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {filteredData.length} {viewType === "deleted" ? "deleted" : ""}{" "}
                trade{filteredData.length !== 1 ? "s" : ""} found
              </Typography>
            </Box>

            {transformedData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No {viewType === "deleted" ? "Deleted" : "Active"} Trade Data
                  Available
                </Typography>
                <Typography variant="body2">
                  {viewType === "deleted"
                    ? "No deleted trades found for the selected date."
                    : "Trades will appear here once they are added to the system."}
                </Typography>
              </Box>
            ) : filteredData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="h5" sx={{ mb: 1 }}>
                  No {viewType === "deleted" ? "Deleted" : "Active"} Trades
                  Found for {shortFormattedDate}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {viewType === "deleted"
                    ? "No deleted trades found for this date. Try selecting a different date."
                    : "Try selecting a different date or check if there are trades on other dates."}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedDate(new Date())}
                  sx={{ textTransform: "none" }}
                >
                  Go to Today
                </Button>
              </Box>
            ) : (
              <TradeBlotterTable
                data={filteredData}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                ref={dataGridRef}
                onFilterStateChange={setHasActiveFilters}
                onSelectionChange={handleTradeSelectionChange}
                viewType={viewType}
                routeList={routes} // Pass routeList as a prop
              />
            )}
          </Box>
        </Box>

      </Box>

      {/* New Order Dialog */}
      <NewOrderDialog
        open={newOrderDialogOpen}
        onClose={() => setNewOrderDialogOpen(false)}
        onSubmit={handleNewOrderSubmit}
      />

      {/* Bulk Edit Dialog */}
      <BulkEdit
        bulkEdit={bulkEditOpen}
        setBulkEdit={handleBulkEditClose}
        selectionModel={selectedTrades}
      />

      {/* Bulk Delete Dialog - Handles both delete and archive operations */}
      <BulkDelete
        bulkdelete={bulkDeleteOpen}
        setBulkdelete={handleBulkDeleteClose}
        selectionModel={selectedTrades}
        isBulkArchive={isBulkArchive}   // true = archive mode, false = delete mode
        setIsBulkArchive={setIsBulkArchive}
      />

      {/* Delete PopUp Dialog */}
      <DeletePopUp
        openDeletePopup={deletePopUpOpen}
        setDeleteOpenPopup={setDeletePopUpOpen}
        recordForDelete={selectedTrades[0]}
        deleteObj="delete_trade"
        item="trade"
      />

      <EditTradeDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTradeForEdit(null);
        }}
        tradeData={selectedTradeForEdit}
      />

      <ManageFillsDialog
        open={manageFillsDialogOpen}
        onClose={() => {
          setManageFillsDialogOpen(false);
          setSelectedTradeForFills(null);
        }}
        tradeData={selectedTradeForFills}
        onSubmit={handleSubmitFills}
      />

      <SummaryDialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        tradesData={filteredData}
      />
    </>
  );
};

export default TradeBlotter;
